/**
 * Component: Menu Drawer V2
 * Handles interactions for the mobile drawer, including tabs, accordion behavior,
 * and state management for opening/closing.
 */
class MenuDrawerV2 {
  /**
   * Initializes the MenuDrawerV2 component and its internal states.
   */
  constructor() {
    this.selectors = {
      drawer: '#menu-drawer-v2',
      details: '.menu-drawer-v2__details',
      summary: 'summary',
      submenu: '.menu-drawer-v2__submenu',
      tab: '[data-v2-drawer-tab]',
      tabPanel: '[data-v2-drawer-panel]',
      tabsSystem: '.menu-drawer-v2__tabs-system',
      closeBtn: '#MenuDrawer-Close',
      mainToggle: '#Details-menu-drawer-container'
    };

    this.classes = {
      menuOpen: 'menu-open',
      menuOpening: 'menu-opening',
      isActive: 'is-active'
    };

    this.drawer = document.querySelector(this.selectors.drawer);
    if (!this.drawer) return;

    this.init();
  }

  /**
   * Entry point for component initialization.
   */
  init() {
    this._initTabs();
    this._initAccordion();
    this._initOpeningHook();
    this._initCloseButton();
  }

  /**
   * Sets up the custom close button behavior within the drawer.
   * @private
   */
  _initCloseButton() {
    const closeBtn = document.querySelector(this.selectors.closeBtn);
    const mainToggle = document.querySelector(this.selectors.mainToggle);
    if (!closeBtn || !mainToggle) return;

    closeBtn.addEventListener('click', (event) => {
      event.preventDefault();
      const headerDrawer = mainToggle.closest('header-drawer');
      const summary = mainToggle.querySelector(this.selectors.summary);

      if (headerDrawer && typeof headerDrawer.closeMenuDrawer === 'function') {
        headerDrawer.closeMenuDrawer(event, summary);
      } else {
        this._forceCloseDrawer(mainToggle, summary);
      }
    });
  }

  /**
   * Forces the drawer to close if the default component close is not available.
   * @param {HTMLElement} mainToggle The details container.
   * @param {HTMLElement} summary The summary element for focus.
   * @private
   */
  _forceCloseDrawer(mainToggle, summary) {
    mainToggle.removeAttribute('open');
    document.body.classList.remove(this.classes.menuOpen);
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
      summary.focus();
    }
  }

  /**
   * Configures hooks to handle opening events and body class toggling.
   * @private
   */
  _initOpeningHook() {
    const mainToggle = document.querySelector(this.selectors.mainToggle);
    if (!mainToggle) return;

    const summary = mainToggle.querySelector(this.selectors.summary);
    if (summary) {
      summary.addEventListener('click', () => {
        if (!mainToggle.hasAttribute('open')) {
          // Small delay to ensure internal DOM state updates
          setTimeout(() => this.resetToFirstOpen(), 10);
        }
      });
    }

    mainToggle.addEventListener('toggle', () => {
      const isOpen = mainToggle.hasAttribute('open');
      document.body.classList.toggle(this.classes.menuOpen, isOpen);
    });
  }

  /**
   * Resets the drawer to show the first menu item as open by default.
   */
  resetToFirstOpen() {
    const allDetails = this.drawer.querySelectorAll(this.selectors.details);
    allDetails.forEach((details, index) => {
      const summary = details.querySelector(this.selectors.summary);
      const expandOnMobile = details.getAttribute('data-expand-on-mobile');
      const isOpen = index === 0;

      if (isOpen && expandOnMobile === 'true') {
        details.setAttribute('open', 'true');
        details.classList.add(this.classes.menuOpening);
      } else {
        details.removeAttribute('open');
        details.classList.remove(this.classes.menuOpening);
      }

      if (summary) {
        summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    });
  }

  /**
   * Initializes accordion behavior, ensuring only one submenu is open at a time.
   * @private
   */
  _initAccordion() {
    const detailsElements = this.drawer.querySelectorAll(this.selectors.details);
    
    detailsElements.forEach(details => {
      const summary = details.querySelector(this.selectors.summary);
      if (!summary) return;

      summary.addEventListener('click', () => {
        // If already open, let it close or stay open naturally
        if (details.hasAttribute('open')) return;

        // Close all other open details
        detailsElements.forEach(other => {
          if (other !== details && other.hasAttribute('open')) {
            other.removeAttribute('open');
            other.classList.remove(this.classes.menuOpening);
            const otherSummary = other.querySelector(this.selectors.summary);
            if (otherSummary) {
              otherSummary.setAttribute('aria-expanded', 'false');
            }
          }
        });
      });
    });
  }

  /**
   * Configures tab switching logic within the drawer submenus.
   * @private
   */
  _initTabs() {
    // Prevent event bubbling on submenus to avoid closing unintended details
    this.drawer.querySelectorAll(this.selectors.submenu).forEach(submenu => {
      submenu.addEventListener('click', (event) => {
        if (event.target.closest(this.selectors.tab)) return;
        event.stopPropagation();
      });

      submenu.addEventListener('focusout', (event) => {
        event.stopPropagation();
      }, true);
    });

    // Handle tab interactions
    this.drawer.querySelectorAll(this.selectors.tabsSystem).forEach(container => {
      const tabs = Array.from(container.querySelectorAll(this.selectors.tab));
      
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const category = tab.getAttribute('data-v2-drawer-tab');
          this._activateTab(container, tab, category);
        });

        tab.addEventListener('keydown', (event) => {
          let newIndex = -1;

          if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            newIndex = index === 0 ? tabs.length - 1 : index - 1;
          } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            newIndex = index === tabs.length - 1 ? 0 : index + 1;
          } else if (event.key === 'Home') {
            newIndex = 0;
          } else if (event.key === 'End') {
            newIndex = tabs.length - 1;
          }

          if (newIndex !== -1) {
            event.preventDefault();
            const newTab = tabs[newIndex];
            const newCategory = newTab.getAttribute('data-v2-drawer-tab');
            this._activateTab(container, newTab, newCategory, true);
          }
        });
      });
    });
  }

  /**
   * Internal logic for activating a specific tab and panel.
   * @param {HTMLElement} container The tabs system container.
   * @param {HTMLElement} activeTab The tab element to activate.
   * @param {string} category The category identifier.
   * @param {boolean} shouldFocus Optional: focus the tab after activation.
   * @private
   */
  _activateTab(container, activeTab, category, shouldFocus = false) {
    const allTabs = Array.from(container.querySelectorAll(this.selectors.tab));
    allTabs.forEach(btn => {
      const isActive = btn === activeTab;
      btn.classList.toggle(this.classes.isActive, isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive && shouldFocus) {
        btn.focus();
      }
    });

    const allPanels = Array.from(container.querySelectorAll(this.selectors.tabPanel));
    allPanels.forEach(panel => {
      const panelCategory = panel.getAttribute('data-v2-drawer-panel');
      const isActive = panelCategory === category;
      panel.classList.toggle(this.classes.isActive, isActive);
      panel.hidden = !isActive;
    });
  }
}

/**
 * Initialization on DOMContentLoaded
 */
document.addEventListener('DOMContentLoaded', () => {
  new MenuDrawerV2();
});

